import { usePage } from "@inertiajs/react"
import { Navigation } from "app/navigation"
import React, { useEffect } from "react"
import { Messages } from "./components/messages"
import { ApprovalBanner } from "app/account/approval-banner"
import { FreeOverLimitBanner } from "./components/free-over-limit-banner"
import { CancellationPendingBanner } from "./components/cancellation-pending-banner"
import { UnconfirmedUserEmailBanner } from "app/account/unconfirmed-user-email-banner"
import { UnconfirmedSendAddressBanner } from "app/account/unconfirmed-send-address-banner"
import { UnpaidBanner } from "./components/unpaid-banner"
import { TrialEndedBanner } from "./components/trial-ended-banner"
import { RequiredInfoBanner } from "./components/required-info-banner"
import {
  reportAndReload,
  clearReloadGuard
} from "./layout-config-sentry-context"

const RELOAD_KEY = "inertia_header_error_reload_count"

/**
 * Error boundary for InertiaPageHeader. On crash:
 * 1. Reports to Sentry with full layoutConfig + navigation context.
 * 2. Triggers a full page reload so the user gets a fresh server response
 *    (recovers from stale bundle / incomplete layoutConfig on the first attempt).
 * 3. If still crashing after one reload, renders a fallback rather than looping.
 */
class InertiaPageHeaderErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error, info) {
    const reloading = reportAndReload(
      error,
      info,
      this.props.pageContext,
      RELOAD_KEY
    )
    if (!reloading) {
      this.setState({ hasError: true })
    }
  }

  render() {
    if (this.state.hasError) return <InertiaPageHeaderErrorFallback />
    return this.props.children
  }
}

function InertiaPageHeaderErrorFallback() {
  return (
    <div className="alert alert-warning m-0" role="alert">
      Couldn&apos;t load navigation. Try refreshing the page.
    </div>
  )
}

/**
 * InertiaPageHeader renders all top-of-page layout elements that were previously
 * rendered server-side in the Rails application layout.
 *
 * This includes navigation, admin header, flash messages, and various alert banners.
 * It reads the shared layout configuration from Inertia props and conditionally
 * renders components based on the current page's requirements.
 */
export function InertiaPageHeader() {
  const page = usePage()
  const {
    props: { layoutConfig }
  } = page

  // Clear the reload guard once the component mounts cleanly, so a crash on
  // a completely different page later doesn't find a stale counter and skip
  // the recovery reload.
  useEffect(() => {
    clearReloadGuard(RELOAD_KEY)
  }, [])

  // Pass Inertia page context to the error boundary so it's available in
  // componentDidCatch without needing to re-call usePage() (hooks can't be used
  // in class components).
  const pageContext = {
    layoutConfig,
    url: page.url,
    component: page.component,
    version: page.version
  }

  return (
    <InertiaPageHeaderErrorBoundary pageContext={pageContext}>
      <InertiaPageHeaderContent layoutConfig={layoutConfig} />
    </InertiaPageHeaderErrorBoundary>
  )
}

function InertiaPageHeaderContent({ layoutConfig }) {
  return (
    <>
      {layoutConfig.components.navbar.visible && (
        <Navigation {...layoutConfig.components.navbar.data} />
      )}

      {layoutConfig.components.messages.visible && (
        <Messages {...layoutConfig.components.messages.data} />
      )}

      {layoutConfig.components.inReview.visible && (
        <ApprovalBanner {...layoutConfig.components.inReview.data} />
      )}

      {layoutConfig.components.freeOverLimit.visible && (
        <FreeOverLimitBanner {...layoutConfig.components.freeOverLimit.data} />
      )}

      {layoutConfig.components.cancellationPending.visible && (
        <CancellationPendingBanner
          {...layoutConfig.components.cancellationPending.data}
        />
      )}

      {layoutConfig.components.unconfirmedUserEmail.visible && (
        <UnconfirmedUserEmailBanner
          {...layoutConfig.components.unconfirmedUserEmail.data}
        />
      )}

      {layoutConfig.components.unconfirmedSendAddress.visible && (
        <UnconfirmedSendAddressBanner
          {...layoutConfig.components.unconfirmedSendAddress.data}
        />
      )}

      {layoutConfig.components.unpaid.visible && <UnpaidBanner />}

      {layoutConfig.components.trialEnded.visible && <TrialEndedBanner />}

      {layoutConfig.components.requiredInfo.visible && (
        <RequiredInfoBanner {...layoutConfig.components.requiredInfo.data} />
      )}
    </>
  )
}
